import { Box, Stack } from '@mui/material';
import React, { ReactNode } from 'react';
import { StyledCard, StyledCardActions, StyledCardContent } from './ProductCard.style';
import { CardDescription, CardHeader, CardPrice, ProductCardSkeleton, ProductEyebrow } from './components';
import { Product, ProductCardType } from './types';
import { TruncatedText } from '../Typography/Typography.style';
import UptimeImage from 'component/commonComponents/UptimeImage/UptimeImage';
import { useTheme } from 'styled-components';
import DefaultUptimeTheme from 'component/theme/defaultTheme';

export interface ProductCardProps {
  product: Product;
  type?: ProductCardType;
  maxDescLines?: number;
  action?: ReactNode;
  showPrice?: boolean;
  showEyebrow?: 'pcl' | 'marketplace';
  showSpecifications?: boolean;
  eyebrowText?: string;
  reasonText?: string;
  onCardClick?: (product: Product) => void;
  locale?: any;
}

const ProductCard = (props: ProductCardProps) => {
  const { product, maxDescLines, type = 'narrow', action, showPrice, showEyebrow, showSpecifications, eyebrowText = '', reasonText = '', onCardClick, locale } = props;
  const theme = useTheme() as typeof DefaultUptimeTheme;
  const picture = product?.pictures?.[0] || {};
  const pictureUrl = picture.large || picture.medium || picture.small || '';
  const isWide = type === 'wide';

  if (!product) return null;

  return (
    <StyledCard elevation={0} type={type} onClick={() => onCardClick?.(product)}>
      <UptimeImage
        src={pictureUrl}
        alt={product.name}
        title={product.name}
        width="100%"
        style={{ backgroundSize: "contain", objectFit: "contain", aspectRatio:"1" }}
        wrapperProps={{
          style: {
            display: 'flex',
            justifyContent: 'center',
            alignSelf: 'center',
            width: isWide ? '50%' : 180,
            flexShrink: 0,
            paddingRight: isWide ? `${theme.uptimeSpace[12]}px` : 0,
            marginBottom: isWide ? 0 : `${theme.uptimeSpace[8]}px`,
          }
        }}
      />

      {showEyebrow === 'pcl' && (
        <Box display="inline-block" height="24px">
          <ProductEyebrow
            text={eyebrowText}
            reason={reasonText}
            locale={locale?.commonStatus}
            id="chip-productcard-approval_status"
          />
        </Box>
      )}

      <Stack direction="column" flex={1}>
        <TruncatedText
          maxlines={1}
          variant="overline"
          color="text.secondary"
          textTransform="uppercase"
          letterSpacing={0.2}
          sx={{ height: '32px' }}
        >
          {product.brand.name}
        </TruncatedText>

        <CardHeader
          title={
            <TruncatedText maxlines={1} variant="h5" title={product.name} width="fit-content">
              {product.name}
            </TruncatedText>
          }
          subheader={
            <CardDescription
              product={product}
              maxDescLines={maxDescLines}
              showSpecifications={showSpecifications}
              locale={locale}
            />
          }
        />

        <StyledCardContent>
          {showEyebrow === 'marketplace' && (
            <ProductEyebrow
              text={product.mock?.approval_status || ''}
              id="chip-productcard-approval_status"
            />
          )}
        </StyledCardContent>

        <CardPrice price={product.mock?.price} currency={product.currency} showPrice={showPrice} />

        {action && (
          <StyledCardActions onClick={(e) => e.stopPropagation()}>{action}</StyledCardActions>
        )}
      </Stack>
    </StyledCard>
  );
};

ProductCard.Skeleton = ProductCardSkeleton;

export default ProductCard;
